import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBoundDashboardStore from '../../store/dashboardGlobalStore';
import DeliveryDashboardSideCard from '../DeliveryDashboardSideCard';
import PIEText from '@src/shared/components/PIEText';
import DeliveryDashboardBottomSheet from '../DeliveryDashboardBottomSheet';
import { IOrder } from '../../models/model-interface';
import DashboardOrderCard from '../DashboardOrderCard';
import { INITIAL_ZOOM_LEVEL } from '../../models/model-constants';
import { DeliveryDashboardScreens, OrderPaymentStatus } from '../../models/model-enum';

import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { globalStyles } from 'snacks-design-system';
import OrderDetailAdditionalInformation from './components/OrderDetailAdditionalInformation';
import TagPreOrder from '../DashboardCustomTag/components/TagPreOrder';
import OrderDetailUserComment from './components/OrderDetailUserComment';
import OrderDetailItemList from './components/OrderDetailItemList';
import { useShallow } from 'zustand/react/shallow';
import DashboardSideCardContentAutoFit from '../DashboardSideCardContentAutoFit';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { useDashboardMapStore } from '../../store/dashboardMapStore';
import { useApplicationUiStore } from '@src/shared/store/ui-store/application-ui-store';
import useDashboardStore from '../../hooks/use-dashboard-store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
const { xdsSpacingB, xdsSpacingC, xdsSpacingD } = globalStyles;

export const DashboardOrderDetail = () => {
  const { isResolutionMobile } = useDeviceDetect();
  const { t } = useTranslation();
  const { getOrderByHash, getOrderEtaByUUID } = useBoundDashboardStore();
  const { orderList } = useDashboardStore();
  const { activeOrderHash, setActiveScreen, clearActiveOrder, setActiveOrderHash } =
    useDashboardUIStore();
  const [order, setOrder] = useState<IOrder | null>();
  const [setMapZoomLevel] = useDashboardMapStore(useShallow((state) => [state.setMapZoomLevel]));
  const { appDomElementClientHeightRef } = useApplicationUiStore();

  useEffect(() => {
    if (activeOrderHash) {
      const retrievedOrder = getOrderByHash(activeOrderHash);
      if (retrievedOrder) {
        setOrder(retrievedOrder);
      }
    }
  }, [activeOrderHash, orderList]);

  const isPreOrder = (orderIsPreorder?: boolean) => !!orderIsPreorder;

  const renderContent = () => {
    return (
      <PIEComponentUtil marginY={xdsSpacingB}>
        <DashboardOrderCard
          elevation={false}
          uuid={order?.uuid || ''}
          orderStatus={order?.status}
          customerName={order?.customer.lastName || ''}
          courier={order?.courier}
          isOrderDetail={order?.hash === activeOrderHash}
          city={order?.customer.address.city || ''}
          postCode={order?.customer.address.postcode || ''}
          updateDate={order?.updateDate}
          isPaid={order?.payment?.status === OrderPaymentStatus.Paid}
          isContainAlcohol={!!order?.hasAlcohol}
          address={`${order?.customer.address.street} ${order?.customer.address.streetNumber}`}
          dueTime={order?.expectedDeliveryTime}
          isOrderDetailView={activeOrderHash ? true : false}
          orderETA={order?.uuid ? getOrderEtaByUUID(order.uuid) : false}
          isPreOrder={isPreOrder(order?.isPreorder)}
        />
        {(order?.customer.address.floor || order?.customer.address.apartmentNumber || isPreOrder(order?.isPreorder)) && (
          <PIEComponentUtil marginBottom={xdsSpacingC}>
            <OrderDetailAdditionalInformation
              floor={order?.customer.address.floor}
              apartmentNumber={order?.customer.address.apartmentNumber}
              bottomSlot={isPreOrder(order?.isPreorder) ? <TagPreOrder /> : undefined}
            />
          </PIEComponentUtil>
        )}
        {order?.comments && (
          <PIEComponentUtil marginBottom={xdsSpacingC}>
            <OrderDetailUserComment comment={order.comments} />
          </PIEComponentUtil>
        )}
        {order?.items && order.items.length && <OrderDetailItemList items={[...order.items]} />}
      </PIEComponentUtil>
    );
  };

  return (
    (!isResolutionMobile && (
      <DeliveryDashboardSideCard
        header={
          <PIEText
            size="var(--xds-font-size-20)"
            align="left"
            text={t('order.order_detail_screen_title')}
            fontWeight="var(--xds-font-weight-bold)"
          ></PIEText>
        }
        showBackButton={true}
        onBackButton={() => {
          clearActiveOrder();
          setMapZoomLevel(INITIAL_ZOOM_LEVEL);
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
        }}
      >
        <DashboardSideCardContentAutoFit
          config={{
            documentNavigationHeaderHeight:
              appDomElementClientHeightRef.appDomElementNavigationHeader as number,
            sectionOffsetA: appDomElementClientHeightRef.domElementOffsetA,
            contentItemHeight: 148,
          }}
        >
          {renderContent()}
        </DashboardSideCardContentAutoFit>
      </DeliveryDashboardSideCard>
    )) || (
      <DeliveryDashboardBottomSheet
        showCloseButton={true}
        onClose={() => {
          setMapZoomLevel(INITIAL_ZOOM_LEVEL);
          setActiveOrderHash(null);
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
        }}
        onBottomSheetHeight={() => {}}
        initialSnap={({ maxHeight }) => maxHeight / 2}
        header={
          <PIEText
            size="var(--xds-font-size-20)"
            align="left"
            text={t('order.order_detail_screen_title')}
            fontWeight="var(--xds-font-weight-bold)"
          ></PIEText>
        }
      >
        <PIEComponentUtil paddingX={xdsSpacingD}>{renderContent()}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    )
  );
};

export default DashboardOrderDetail;
